class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Debounce ONLY free-text inputs (the price-range fields). Checkbox / pill /
    // swatch taps fire immediately so the drawer feels responsive — the old flat
    // 800ms debounce made every tap sit dead for ~0.8s before anything happened,
    // the single biggest "clunky / not fluid" complaint.
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      const t = event.target;
      const isText = !!(t && (t.type === 'text' || t.type === 'number' || (t.closest && t.closest('price-range'))));
      if (isText) this.debouncedOnSubmit(event);
      else this.onSubmitHandler(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.requestToken = (FacetFiltersForm.requestToken || 0) + 1;
    const requestToken = FacetFiltersForm.requestToken;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cachedHtml = FacetFiltersForm.getCachedSection(url);

      cachedHtml !== null
        ? FacetFiltersForm.renderSectionFromCache(cachedHtml, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestToken);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static getCachedSection(url) {
    if (!FacetFiltersForm.filterData.has(url)) return null;

    const html = FacetFiltersForm.filterData.get(url);
    // Map preserves insertion order. Reinsert a hit so the first key remains
    // the least-recently-used entry for bounded eviction.
    FacetFiltersForm.filterData.delete(url);
    FacetFiltersForm.filterData.set(url, html);
    return html;
  }

  static cacheSection(url, html) {
    // One entry per URL: a newer successful render replaces the old response
    // instead of leaving duplicate, permanently-shadowed array entries.
    FacetFiltersForm.filterData.delete(url);
    FacetFiltersForm.filterData.set(url, html);

    if (FacetFiltersForm.filterData.size > FacetFiltersForm.filterDataLimit) {
      const leastRecentlyUsedUrl = FacetFiltersForm.filterData.keys().next().value;
      FacetFiltersForm.filterData.delete(leastRecentlyUsedUrl);
    }
  }

  static renderSectionFromFetch(url, event, requestToken) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Section render failed: HTTP ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        if (requestToken !== FacetFiltersForm.requestToken) return;
        const viewState = FacetFiltersForm.captureViewState();
        setTimeout(() => {
          if (requestToken !== FacetFiltersForm.requestToken) return;
          try {
            FacetFiltersForm.renderSection(html, event, viewState);
            FacetFiltersForm.cacheSection(url, html);
          } catch (error) {
            FacetFiltersForm.handleFetchError(error, requestToken);
          }
        }, 0);
      })
      .catch((error) => {
        FacetFiltersForm.handleFetchError(error, requestToken);
      });
  }

  static handleFetchError(error, requestToken) {
    // A failed fetch (or render throw) used to leave the grid dimmed with
    // spinners running forever. Restore the previous, still-usable results.
    console.error(error);
    if (requestToken !== FacetFiltersForm.requestToken) return;
    FacetFiltersForm.clearSoldOutPending();
    FacetFiltersForm.clearLoadingState();
    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.showFetchError();
  }

  static clearLoadingState() {
    const grid = document.getElementById('ProductGridContainer');
    const collection = grid ? grid.querySelector('.collection') : null;
    if (collection) collection.classList.remove('loading');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) container.classList.remove('loading');
    if (containerDesktop) containerDesktop.classList.remove('loading');
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static showFetchError() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    let note = grid.querySelector('.facets-fetch-error');
    if (!note) {
      note = document.createElement('div');
      note.className = 'facets-fetch-error';
      note.setAttribute('role', 'alert');
      note.style.cssText =
        'padding:1rem 1.5rem;margin:0 auto 1rem;max-width:60rem;border:1px solid #b91c1c;border-radius:0.6rem;color:#b91c1c;font-size:1.3rem;text-align:center;';
      grid.prepend(note);
    }
    note.textContent = "Couldn't update the results — please check your connection and try again.";
    setTimeout(() => {
      if (note.parentNode) note.remove();
    }, 6000);
  }

  static renderSectionFromCache(html, event) {
    FacetFiltersForm.requestToken = (FacetFiltersForm.requestToken || 0) + 1;
    const requestToken = FacetFiltersForm.requestToken;
    const viewState = FacetFiltersForm.captureViewState();
    setTimeout(() => {
      if (requestToken !== FacetFiltersForm.requestToken) return;
      FacetFiltersForm.renderSection(html, event, viewState);
    }, 0);
  }

  static renderSection(html, event, viewState) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // renderFilters can adopt or mutate facet nodes in parsedHTML. The grid
    // and count are sibling subtrees, so this consumer order is load-bearing.
    FacetFiltersForm.renderFilters(parsedHTML, event, viewState);
    FacetFiltersForm.renderProductGridContainer(parsedHTML, viewState);
    FacetFiltersForm.renderProductCount(parsedHTML);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static captureViewState() {
    // Capture, BEFORE the re-render, the scroll position and the absolute
    // document Y of the FIRST mobile filter section. renderProductGridContainer
    // restores scroll by however much that anchor shifts, so anything added
    // above it (e.g. a filter chip appearing at the top of the drawer) can't
    // push the view down. Anchoring on the element's real position captures the
    // full footprint (padding/margins/borders), not just a raw offsetHeight.
    // Desktop is a no-op: the mobile filter form isn't laid out there.
    const savedScrollY = window.scrollY;
    const savedAnchorDocY = FacetFiltersForm.topmostMobileFilterDocY();

    // Preserve each mobile "Show more" expanded state across the re-render:
    // a non-clicked filter (e.g. Print when you tap Size) has its innerHTML
    // replaced with server HTML where the toggle is unchecked, which would
    // collapse an expanded gallery. Capture now, restore after the swap.
    const savedShowMore = {};
    document.querySelectorAll('.mobile-facets__showmore-toggle').forEach((toggle) => {
      if (toggle.id) savedShowMore[toggle.id] = toggle.checked;
    });

    return { savedScrollY, savedAnchorDocY, savedShowMore };
  }

  static topmostMobileFilterDocY() {
    // Absolute document Y of the visually-topmost mobile filter section (the
    // one directly under the active-chips row — Print here, via order:-1).
    // Whatever gets added above it (e.g. a filter chip) shifts this value;
    // renderProductGridContainer restores scroll by the same amount so the
    // drawer view stays put. Returns null off the mobile drawer.
    let minY = null;
    document.querySelectorAll('#FacetFiltersFormMobile .js-filter').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.height === 0) return;
      const y = rect.top + window.scrollY;
      if (minY === null || y < minY) minY = y;
    });
    return minY;
  }

  static renderProductGridContainer(parsedHTML, viewState) {
    // Keep the drawer view stable through a filter change. Two things move it:
    //  (a) a narrower grid shortens the document -> browser clamps scroll to top;
    //  (b) a filter chip added at the TOP of the drawer pushes content down.
    // Base target = the scroll captured BEFORE the re-render (renderFilters);
    // add the active-chips height delta so a newly added/removed chip cancels
    // out. Fall back to the live scroll if the saved value is missing.
    const preservedScrollY =
      typeof viewState.savedScrollY === 'number' ? viewState.savedScrollY : window.scrollY;

    document.getElementById('ProductGridContainer').innerHTML =
      parsedHTML.getElementById('ProductGridContainer').innerHTML;
    // Fresh results replace the landing grid, so the first-paint sold-out hide is done.
    FacetFiltersForm.clearSoldOutPending();

    // Ease the fresh grid in (opacity only — no layout change, so the scroll
    // anchor below is unaffected). The old grid was dimmed via .loading during
    // the fetch; this crossfades to the new results instead of a hard pop.
    const freshGrid = document.getElementById('ProductGridContainer').querySelector('.collection');
    if (freshGrid) {
      freshGrid.classList.add('facet-grid-in');
      freshGrid.addEventListener('animationend', () => freshGrid.classList.remove('facet-grid-in'), { once: true });
    }

    // The swap detaches everything scripts bound to (e.g. the mobile card
    // carousels initialized by main-collection-product-grid). Let them re-bind.
    document.dispatchEvent(new CustomEvent('facets:rendered'));

    // Hold the drawer view stable through ASYNC reflows. The first chip appears
    // via the empty-guard :has() re-eval a few frames after the swap (and the
    // grid/fonts settle later too), so a one-shot restore is stale. Recompute
    // the anchor delta every frame and re-apply until it stops changing
    // (settled) or a ~1s safety cap. rect.top + scrollY is scroll-invariant so
    // this converges; once settled, scrollTo(sameValue) is a no-op (no jank).
    if (viewState.skipScrollRestore) {
      // Landing default (hide sold out): the swap only adds cards below the ones showing,
      // so leave the scroll alone; a scrollTo here would stop a fling already under way.
    } else if (typeof viewState.savedAnchorDocY !== 'number') {
      // Desktop / drawer closed: the mobile anchor doesn't exist. One-shot
      // restore, no rAF loop and no scroll listeners at all.
      window.scrollTo(0, preservedScrollY);
    } else {
      // Yield to a DELIBERATE user scroll so we never fight momentum/inertial
      // scrolling (that rubber-banding was itself part of the "clunky" feel):
      // abort the hold only on a real >10px drag/wheel AFTER the first few settle
      // frames, so incidental tap-jitter doesn't cancel the late first-chip
      // correction. Keep holding until the chip row has actually laid out.
      const chipRow = document.querySelector('#FacetFiltersFormMobile .active-facets-in-drawer');
      const chipsSettled = () =>
        !chipRow ||
        chipRow.getBoundingClientRect().height > 0 ||
        !chipRow.querySelector('facet-remove:not(.active-facets__button-wrapper)');
      let holdFrames = 0;
      let lastDelta = null;
      let stableFrames = 0;
      let userInterrupted = false;
      let touchStartY = null;
      const onTouchStart = (e) => {
        touchStartY = e.touches && e.touches[0] ? e.touches[0].clientY : null;
      };
      const onTouchMove = (e) => {
        if (holdFrames < 3) return;
        const y = e.touches && e.touches[0] ? e.touches[0].clientY : null;
        if (touchStartY !== null && y !== null && Math.abs(y - touchStartY) > 10) userInterrupted = true;
      };
      const onWheel = () => {
        if (holdFrames >= 3) userInterrupted = true;
      };
      const cleanup = () => {
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('wheel', onWheel);
      };
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: true });
      window.addEventListener('wheel', onWheel, { passive: true });
      const holdAnchor = () => {
        if (userInterrupted) {
          cleanup();
          return;
        }
        const nowDocY = FacetFiltersForm.topmostMobileFilterDocY();
        const delta = nowDocY !== null ? nowDocY - viewState.savedAnchorDocY : 0;
        window.scrollTo(0, preservedScrollY + delta);
        holdFrames += 1;
        if (lastDelta !== null && Math.abs(delta - lastDelta) < 1) stableFrames += 1;
        else stableFrames = 0;
        lastDelta = delta;
        if ((stableFrames < 5 || !chipsSettled()) && holdFrames < 45) requestAnimationFrame(holdAnchor);
        else cleanup();
      };
      holdAnchor();
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(parsedHTML) {
    const count = parsedHTML.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event, viewState) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        // The re-skin hides .mobile-facets__close-button (display:none), and
        // focus() on a hidden element silently drops focus to <body> — anchor
        // on the always-visible summary instead.
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `summary.mobile-facets__summary`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus({ preventScroll: true });
      }
    }

    // Restore the "Show more" expanded state captured at the top of this method
    // so an expanded gallery (e.g. Print) stays open when OTHER filters change.
    // Setting .checked programmatically fires no event, so it won't re-trigger
    // filtering — it just re-applies the CSS :checked expand.
    if (viewState.savedShowMore) {
      Object.keys(viewState.savedShowMore).forEach((id) => {
        const toggle = document.getElementById(id);
        if (toggle) {
          toggle.checked = viewState.savedShowMore[id];
          // Programmatic .checked fires no change event, so also sync the
          // label's aria-expanded (its inline onchange handles user toggles).
          const label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
          if (label) label.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
        }
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];
    // The In Stock pill lives in these rows: keep keyboard focus on it through the swap.
    const focused = document.activeElement;
    const refocusId =
      focused && focused.classList && focused.classList.contains('dm-instock-pill__input') ? focused.id : '';

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    if (refocusId) {
      const pill = document.getElementById(refocusId);
      if (pill) pill.focus({ preventScroll: true });
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = new Map();
FacetFiltersForm.filterDataLimit = 12;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
if (!document.querySelector('dm-filter-chips')) FacetFiltersForm.setListeners();

// In Stock pill (owner 2026-10-24). snippets/facets.liquid renders Shopify's Availability
// filter as one "In Stock" pill (a checkbox) first in the selected-filter chips row and marks
// it data-dm-default-on on page 1 of collections with stock in them; this turns it on once per
// landing, without the loading overlay or a scroll restore. The URL is left as it is (a
// history change would count as a second page view in pixels that track them); Back still
// restores the filtered list via searchParamsInitial. Sold-out cards are hidden at first paint
// (html.dm-soldout-pending, set by the inline script in facets.liquid) so the swap only fills
// the page out. A shopper who turns it off keeps everything showing for the rest of the visit.
FacetFiltersForm.soldOutOptOutKey = 'dmShowSoldOut';

FacetFiltersForm.clearSoldOutPending = function () {
  document.documentElement.classList.remove('dm-soldout-pending');
};

FacetFiltersForm.applySoldOutDefault = function () {
  const input = document.querySelector('.dm-instock-pill__input[data-dm-default-on]');
  const grid = document.getElementById('product-grid');
  const params = new URLSearchParams(window.location.search);
  let optedOut = false;
  try {
    optedOut = sessionStorage.getItem(FacetFiltersForm.soldOutOptOutKey) === '1';
  } catch (e) {}
  // data-dm-active = the server already rendered the in-stock list.
  if (!input || !grid || input.hasAttribute('data-dm-active') || params.has(input.name) || optedOut) {
    FacetFiltersForm.clearSoldOutPending();
    return;
  }
  // Turn the pills on while the in-stock list loads, so turning one off in that window is a
  // real "show everything" (the facets form submits it and drops this fetch).
  const boxes = document.querySelectorAll('.dm-instock-pill__input');
  const setBoxes = (checked) => boxes.forEach((box) => (box.checked = checked));
  setBoxes(true);

  params.delete('page'); // the in-stock list starts again at page 1
  params.append(input.name, input.value);
  const searchParams = params.toString();
  const url = `${window.location.pathname}?section_id=${grid.dataset.id}&${searchParams}`;
  FacetFiltersForm.requestToken = (FacetFiltersForm.requestToken || 0) + 1;
  const requestToken = FacetFiltersForm.requestToken;

  // The first card links a few stylesheets (deferred, media=print until onload). The swap
  // would drop and re-add them, unstyling badges and ratings for a frame; a head copy keeps
  // them applied through this and every later swap.
  document.querySelectorAll('#ProductGridContainer link[rel="stylesheet"]').forEach((link) => {
    const inHead = Array.prototype.some.call(
      document.head.querySelectorAll('link[rel="stylesheet"]'),
      (headLink) => headLink.href === link.href
    );
    if (inHead) return;
    const keep = document.createElement('link');
    keep.rel = 'stylesheet';
    keep.href = link.href;
    document.head.appendChild(keep);
  });

  fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Section render failed: HTTP ${response.status}`);
      return response.text();
    })
    .then((html) => {
      // A filter tapped meanwhile owns the page now.
      if (requestToken !== FacetFiltersForm.requestToken) return;
      // In-stock products the grid cannot show yet (public reveal date not reached) still
      // count as in stock; never open on an empty grid because of them.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (!parsed.querySelector('#product-grid .grid__item')) {
        setBoxes(false);
        FacetFiltersForm.clearSoldOutPending();
        return;
      }
      const viewState = FacetFiltersForm.captureViewState();
      viewState.skipScrollRestore = true;
      FacetFiltersForm.renderSection(html, null, viewState);
      FacetFiltersForm.cacheSection(url, html);
      // Same cards in the same order, just more of them: no crossfade over the ones showing.
      const freshGrid = document.querySelector('#ProductGridContainer .collection');
      if (freshGrid) freshGrid.classList.remove('facet-grid-in');
      FacetFiltersForm.searchParamsInitial = searchParams;
      FacetFiltersForm.searchParamsPrev = searchParams;
    })
    .catch((error) => {
      console.error(error);
      setBoxes(false);
      FacetFiltersForm.clearSoldOutPending();
    });
};

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input || !input.classList || !input.classList.contains('dm-instock-pill__input')) return;
  // Turning it on or off (even mid-landing) decides the state; the facets form submits it.
  FacetFiltersForm.clearSoldOutPending();
  try {
    if (input.checked) sessionStorage.removeItem(FacetFiltersForm.soldOutOptOutKey);
    else sessionStorage.setItem(FacetFiltersForm.soldOutOptOutKey, '1');
  } catch (e) {}
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('dm-filter-chips')) FacetFiltersForm.applySoldOutDefault();
  });
} else {
  if (!document.querySelector('dm-filter-chips')) FacetFiltersForm.applySoldOutDefault();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
